import { useEffect, useState, useMemo } from "react"
import API from "@/common/API"
import { parsedUser } from "@/common/GetCurrentUser"

export interface MentionUser {
  id: string
  name: string
  role?: string
  email?: string
  avatar?: string
}

let cachedUsers: MentionUser[] | null = null
let fetchPromise: Promise<MentionUser[]> | null = null

export const fetchOrganizationMentionUsers = async (): Promise<MentionUser[]> => {
  if (cachedUsers && cachedUsers.length > 0) {
    return cachedUsers
  }

  if (fetchPromise) {
    return fetchPromise
  }

  fetchPromise = (async () => {
    const currentUser = parsedUser()
    const orgId = currentUser?.OrganizationId || ""
    const userMap = new Map<string, MentionUser>()

    // Current user can always be resolved or self-excluded
    if (currentUser?.ID) {
      const selfName = String(`${currentUser.FirstName || ""} ${currentUser.LastName || ""}`.trim() || currentUser.Name || "Tú")
      userMap.set(String(currentUser.ID), {
        id: String(currentUser.ID),
        name: selfName,
        email: currentUser.Email ? String(currentUser.Email) : undefined,
        role: "Tú",
      })
    }

    // 1. Try agents/Summary
    try {
      const agentsRes = await API.getAction(
        "agents/Summary",
        `OrganizationId=${orgId}&Page=0&Quantity=100`
      )
      if (agentsRes.data?.status === "ok" && Array.isArray(agentsRes.data.response)) {
        agentsRes.data.response.forEach((a: any) => {
          const id = String(a.id || a.agentId || a.userId || "")
          if (!id) return
          const first = a.firstName || ""
          const last = a.lastName || ""
          const fullName = `${first} ${last}`.trim() || a.name || a.userName || "Usuario"
          const roleStr = (a.role || a.jobTitle || "").toLowerCase()
          const roleDisplay = roleStr.includes("driver") || roleStr.includes("chofer")
            ? "Chofer"
            : roleStr.includes("admin")
            ? "Admin"
            : roleStr.includes("superv")
            ? "Supervisor"
            : "Vendedor"

          userMap.set(id, {
            id,
            name: fullName,
            role: roleDisplay,
            email: a.email,
            avatar: a.avatar || a.pic,
          })
        })
      }
    } catch (e) {
      console.warn("Could not load agents for mentions:", e)
    }

    // 2. Try Account/List as fallback/enrichment
    if (userMap.size <= 1) {
      try {
        const accRes = await API.getAction("Account/List", "")
        if (Array.isArray(accRes.data)) {
          accRes.data.forEach((acc: any) => {
            const id = String(acc.id || acc.Id || "")
            if (!id || userMap.has(id)) return
            const name = `${acc.firstName || acc.FirstName || ""} ${acc.lastName || acc.LastName || ""}`.trim() || acc.email || "Usuario"
            userMap.set(id, {
              id,
              name,
              email: acc.email || acc.Email,
              role: "Equipo",
            })
          })
        }
      } catch (e) {
        console.warn("Could not load accounts for mentions:", e)
      }
    }

    cachedUsers = Array.from(userMap.values())
    fetchPromise = null
    return cachedUsers
  })()

  return fetchPromise
}

export const useMentionUsers = (initialUsers?: MentionUser[]) => {
  const [users, setUsers] = useState<MentionUser[]>(() => cachedUsers || initialUsers || [])
  const [loading, setLoading] = useState<boolean>(!cachedUsers || cachedUsers.length === 0)

  useEffect(() => {
    let isMounted = true
    fetchOrganizationMentionUsers().then((loaded) => {
      if (!isMounted) return
      if (initialUsers && initialUsers.length > 0) {
        // Merge explicit initialUsers (e.g. from chat conversation members)
        const map = new Map<string, MentionUser>()
        initialUsers.forEach((u) => map.set(u.id, u))
        loaded.forEach((u) => {
          if (!map.has(u.id)) map.set(u.id, u)
        })
        setUsers(Array.from(map.values()))
      } else {
        setUsers(loaded)
      }
      setLoading(false)
    })

    return () => {
      isMounted = false
    }
  }, [initialUsers])

  return { users, loading }
}
